// fetchVideoTranscript: metadata + timed transcript of a YouTube video, in Node or in the browser.
//
// Strategy (verified Oct 2026; youtubei.js' getTranscript()/get_transcript is dead without a
// BotGuard attestation -> HTTP 400 "Precondition check failed"):
//   1. WEB /next            -> title, channel, date, description, chapters, visitorData   (in parallel)
//   2. /player, native client (ANDROID -> IOS -> ANDROID_VR with visitorData)
//                           -> videoDetails + caption tracks WITHOUT the PO-token gate (exp=xpe)
//   3. GET caption track baseUrl with fmt=json3 -> segments
// The WEB /player is not used: its caption URLs need a Proof-of-Origin token (empty body otherwise).

import type { VideoTranscript } from '../api';
import { YouTubeError, isAbortError } from './errors';
import {
  captionTracks,
  chaptersFromDescription,
  classifyPlayability,
  metaFromNext,
  metaFromPlayer,
  nextSaysUnavailable,
  originalLanguage,
  selectCaptionTrack,
  visitorDataFromResponse,
} from './extract';
import { createFetchTransport, type InnertubeTransport, type PlayerClientName, type TransportOptions } from './innertube';
import { isValidVideoId } from './videoId';
import { needsPoToken, parseTimedText, timedtextUrl } from './timedtext';

export const DEFAULT_PLAYER_CLIENTS: readonly PlayerClientName[] = ['ANDROID', 'IOS', 'ANDROID_VR'];

export interface FetchTranscriptOptions extends TransportOptions {
  /**
   * Fallback language (e.g. the UI language) used only when no track in the video's original
   * language exists. The original language always wins: the summary model translates anyway.
   */
  preferredLang?: string;
  /** Player clients to try, in order. */
  clients?: readonly PlayerClientName[];
  /** Overall timeout in ms (default 20 000). Combined with `signal`. */
  timeoutMs?: number;
  /** Custom transport (server: youtubei.js session with cookies/OAuth, proxy, ...). Ignores fetch/headers options. */
  transport?: InnertubeTransport;
  /** Diagnostics, e.g. `log: (m) => console.debug('[yt]', m)`. */
  log?: (message: string) => void;
}

type Settled<T> = { ok: true; value: T } | { ok: false; error: unknown };

export async function fetchVideoTranscript(videoId: string, options: FetchTranscriptOptions = {}): Promise<VideoTranscript> {
  if (!isValidVideoId(videoId)) {
    throw new YouTubeError('invalid_id', `Not a YouTube video ID: ${JSON.stringify(videoId)}`);
  }
  const log = options.log ?? (() => {});
  const signal = combineSignals(options.signal, options.timeoutMs ?? 20_000);
  const transport = options.transport ?? createFetchTransport({ ...options, signal });
  const clients = options.clients?.length ? options.clients : DEFAULT_PLAYER_CLIENTS;

  const nextPromise: Promise<Settled<unknown>> = transport.next(videoId).then(
    (value) => ({ ok: true, value }),
    (error: unknown) => ({ ok: false, error }),
  );
  const nextResponse = async () => {
    const settled = await nextPromise;
    if (!settled.ok && isAbortError(settled.error)) throw settled.error;
    return settled.ok ? settled.value : undefined;
  };

  let failure: YouTubeError | undefined;
  let visitorData: string | undefined;

  for (const [index, client] of clients.entries()) {
    options.signal?.throwIfAborted();
    if (signal?.aborted) throw new YouTubeError('network', 'YouTube did not answer in time.');
    // The first attempt runs in parallel with /next; later ones reuse its visitorData (ANDROID_VR needs it).
    if ((index > 0 || client === 'ANDROID_VR') && !visitorData) visitorData = visitorDataFromResponse(await nextResponse());

    let player: unknown;
    try {
      player = await transport.player(videoId, client, visitorData);
    } catch (error) {
      if (isAbortError(error)) throw error;
      failure = asYouTubeError(error);
      log(`player ${client}: ${failure.message}`);
      if (failure.reason === 'rate_limited') break;
      continue;
    }

    const playability = classifyPlayability(player);
    const track = selectCaptionTrack(player, options.preferredLang);
    if (!track) {
      if (playability.ok) {
        failure = captionTracks(player).length
          ? new YouTubeError('bad_response', 'Caption tracks could not be read.')
          : new YouTubeError('no_captions', 'This video has no captions/transcript.');
        log(`player ${client}: playable, no caption tracks`);
        break; // all clients see the same captions
      }
      failure = playability.error;
      log(`player ${client}: ${failure.message}`);
      if (playability.final) break;
      continue;
    }

    let body: string;
    try {
      body = await transport.timedtext(timedtextUrl(track.baseUrl));
    } catch (error) {
      if (isAbortError(error)) throw error;
      failure = asYouTubeError(error);
      log(`timedtext ${client}: ${failure.message}`);
      // The timedtext rate limit is per IP ("Sorry..." page, HTTP 429): other clients won't help.
      if (failure.reason === 'rate_limited') break;
      continue;
    }
    const segments = parseTimedText(body);
    if (segments.length === 0) {
      failure = body.trim()
        ? new YouTubeError('bad_response', 'Transcript could not be parsed.')
        : new YouTubeError(
            'po_token_required',
            `YouTube returned an empty transcript${needsPoToken(track.baseUrl) ? ' (track requires a PO token)' : ''}.`,
          );
      log(`timedtext ${client}: ${failure.message}`);
      continue;
    }

    log(`ok via ${client}: ${track.languageCode}${track.kind === 'asr' ? ' (auto)' : ''}, ${segments.length} segments`);
    const next = await nextResponse();
    const fromNext = next ? metaFromNext(next) : null;
    const fromPlayer = metaFromPlayer(player);
    const description = fromPlayer.description || fromNext?.description || undefined;
    return compact({
      videoId,
      title: fromPlayer.title ?? fromNext?.title ?? '',
      channel: fromPlayer.channel ?? fromNext?.channel ?? '',
      channelUrl: fromNext?.channelUrl ?? fromPlayer.channelUrl,
      durationSeconds: fromPlayer.durationSeconds,
      publishedAt: fromNext?.publishedAt,
      thumbnailUrl: fromPlayer.thumbnailUrl ?? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
      description,
      chapters: fromNext?.chapters ?? chaptersFromDescription(description),
      language: track.languageCode,
      sourceLanguage: originalLanguage(player),
      isAutoGenerated: track.kind === 'asr',
      segments,
    });
  }

  // Every client failed. If /next says the video does not exist, report that instead.
  const next = await nextResponse();
  if (failure?.reason !== 'not_found' && next && nextSaysUnavailable(next)) {
    throw new YouTubeError('not_found', 'Video not found or unavailable.');
  }
  throw failure ?? new YouTubeError('bad_response', 'No transcript could be fetched.');
}

function asYouTubeError(error: unknown): YouTubeError {
  if (error instanceof YouTubeError) return error;
  return new YouTubeError('bad_response', error instanceof Error ? error.message : String(error), { cause: error });
}

function combineSignals(signal: AbortSignal | undefined, timeoutMs: number): AbortSignal | undefined {
  if (!(timeoutMs > 0) || typeof AbortSignal.timeout !== 'function') return signal;
  const timeout = AbortSignal.timeout(timeoutMs);
  if (!signal) return timeout;
  return typeof AbortSignal.any === 'function' ? AbortSignal.any([signal, timeout]) : signal;
}

function compact<T extends object>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;
}
